/**
 * 統一時間管理系統
 * 處理所有時間格式的轉換和標準化
 */

// 時間格式類型定義
export type TimeFormat = 
  | 'timestamp'        // Unix timestamp (秒)
  | 'timestampMs'      // Unix timestamp (毫秒)
  | 'timecode'         // HH:MM:SS FF (視頻時間碼)
  | 'timecodeSimple'   // HH:MM:SS (簡單時間碼)
  | 'iso'              // ISO 8601 字符串
  | 'firestore'        // Firestore Timestamp 對象
  | 'date'             // JavaScript Date 對象
  | 'screenshot'       // CheckPlay_00h30m08s_14 格式
  | 'month'            // YYYY-MM 格式

// 標準時間數據結構
export interface StandardTime {
  type: TimeFormat;
  value: any;
  originalValue?: any; // 保留原始值用於調試
}

/**
 * 時間轉換器類
 */
export class TimeConverter {
  private static readonly FRAME_RATE = 30; // 標準幀率
  private static readonly MAX_VIDEO_DURATION = 1000000; // 支持長時間直播，約 278 小時

  /**
   * 將任何時間格式轉換為標準的視頻時間戳（秒）
   */
  static toVideoSeconds(input: any): number | null {
    if (input === null || input === undefined) return null;
    
    // 已經是數字（秒）
    if (typeof input === 'number') {
      if (input > TimeConverter.MAX_VIDEO_DURATION) {
        console.warn('⚠️ 時間戳過大，可能是 Unix timestamp:', input);
        return null;
      }
      return input;
    }

    // 字符串時間碼
    if (typeof input === 'string') {
      return TimeConverter.parseTimecodeToSeconds(input);
    }

    // Firestore Timestamp
    if (input && typeof input === 'object' && input.seconds) {
      return input.seconds;
    }

    // Date 對象
    if (input instanceof Date) {
      return input.getTime() / 1000;
    }

    console.warn('⚠️ 無法識別的時間格式:', typeof input, input);
    return null;
  }

  /**
   * 將視頻時間戳轉換為標準時間碼格式
   */
  static toTimecode(seconds: number): string {
    if (seconds < 0) return '00:00:00 00';
    
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const frames = Math.floor((seconds % 1) * TimeConverter.FRAME_RATE);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} ${frames.toString().padStart(2, '0')}`;
  }

  /**
   * 解析時間碼字符串為秒數
   */
  static parseTimecodeToSeconds(timecode: string): number | null {
    if (!timecode || typeof timecode !== 'string') return null;
    
    // 支援格式: "HH:MM:SS FF" 或 "HH:MM:SS:FF"
    const match = timecode.match(/^(\d{2}):(\d{2}):(\d{2})\s*[: ]\s*(\d{2})$/);
    if (!match) return null;
    
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);
    const frames = parseInt(match[4], 10);
    
    return hours * 3600 + minutes * 60 + seconds + frames / TimeConverter.FRAME_RATE;
  }

  /**
   * 轉換為截圖文件名格式
   */
  static toScreenshotFormat(seconds: number): string {
    if (!seconds || seconds < 0) return 'CheckPlay_00h00m00s_00';
    
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const frames = Math.floor((seconds % 1) * TimeConverter.FRAME_RATE);
    
    return `CheckPlay_${hours.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}m${secs.toString().padStart(2, '0')}s_${frames.toString().padStart(2, '0')}`;
  }

  /**
   * 轉換為月份格式 (YYYY-MM)
   */
  static toMonthFormat(input: any): string {
    let date: Date;
    
    if (input instanceof Date) {
      date = input;
    } else if (typeof input === 'number') {
      date = new Date(input * 1000); // 假設是 Unix timestamp (秒)
    } else if (input && typeof input === 'object' && input.seconds) {
      date = input.toDate(); // Firestore Timestamp
    } else {
      date = new Date(input);
    }
    
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    return `${year}-${month.toString().padStart(2, '0')}`;
  }

  /**
   * 轉換為 ISO 字符串
   */
  static toISOString(input: any): string {
    let date: Date;
    
    if (input instanceof Date) {
      date = input;
    } else if (typeof input === 'number') {
      date = new Date(input * 1000);
    } else if (input && typeof input === 'object' && input.seconds) {
      date = input.toDate();
    } else {
      date = new Date(input);
    }
    
    return date.toISOString();
  }

  /**
   * 驗證時間格式
   */
  static validateTimeFormat(input: any, expectedFormat: TimeFormat): boolean {
    switch (expectedFormat) {
      case 'timestamp':
        return typeof input === 'number' && input > 0 && input < TimeConverter.MAX_VIDEO_DURATION;
      case 'timecode':
        return typeof input === 'string' && /^\d{2}:\d{2}:\d{2} \d{2}$/.test(input);
      case 'firestore':
        return input && typeof input === 'object' && input.seconds;
      case 'date':
        return input instanceof Date && !isNaN(input.getTime());
      default:
        return true;
    }
  }

  /**
   * 智能時間轉換 - 自動識別輸入格式並轉換
   */
  static smartConvert(input: any, targetFormat: TimeFormat): any {
    const videoSeconds = TimeConverter.toVideoSeconds(input);
    if (videoSeconds === null) return null;

    switch (targetFormat) {
      case 'timestamp':
        return videoSeconds;
      case 'timecode':
        return TimeConverter.toTimecode(videoSeconds);
      case 'screenshot':
        return TimeConverter.toScreenshotFormat(videoSeconds);
      case 'iso':
        return new Date(videoSeconds * 1000).toISOString();
      case 'date':
        return new Date(videoSeconds * 1000);
      default:
        return input;
    }
  }
}

/**
 * 時間驗證器
 */
export class TimeValidator {
  /**
   * 檢查是否為有效的視頻時間
   */
  static isValidVideoTime(input: any): boolean {
    const seconds = TimeConverter.toVideoSeconds(input);
    return seconds !== null && seconds >= 0 && seconds <= TimeConverter['MAX_VIDEO_DURATION'];
  }

  /**
   * 檢查是否為有效的時間碼格式
   */
  static isValidTimecode(input: string): boolean {
    return /^\d{2}:\d{2}:\d{2} \d{2}$/.test(input);
  }

  /**
   * 檢查是否為 Firestore Timestamp
   */
  static isFirestoreTimestamp(input: any): boolean {
    return input && typeof input === 'object' && typeof input.seconds === 'number';
  }
}

/**
 * 時間格式化器
 */
export class TimeFormatter {
  /**
   * 格式化顯示時間（用戶界面）
   */
  static formatDisplayTime(input: any, format: 'timecode' | 'simple' = 'timecode'): string {
    const seconds = TimeConverter.toVideoSeconds(input);
    if (seconds === null) return '00:00:00';

    if (format === 'simple') {
      const totalSeconds = Math.floor(seconds);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
      }
    }

    return TimeConverter.toTimecode(seconds);
  }

  /**
   * 格式化相對時間（如 "2分鐘前"）
   */
  static formatRelativeTime(input: any): string {
    const now = new Date();
    let date: Date;
    
    if (input instanceof Date) {
      date = input;
    } else if (typeof input === 'number') {
      date = new Date(input * 1000);
    } else if (input && typeof input === 'object' && input.seconds) {
      date = input.toDate();
    } else {
      date = new Date(input);
    }
    
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}天前`;
    if (diffHours > 0) return `${diffHours}小時前`;
    if (diffMinutes > 0) return `${diffMinutes}分鐘前`;
    return '剛剛';
  }
}

// 導出常用的快捷函數
export const timeUtils = {
  // 轉換函數
  toSeconds: TimeConverter.toVideoSeconds,
  toTimecode: TimeConverter.toTimecode,
  toScreenshot: TimeConverter.toScreenshotFormat,
  toMonth: TimeConverter.toMonthFormat,
  smartConvert: TimeConverter.smartConvert,
  
  // 驗證函數
  isValidVideoTime: TimeValidator.isValidVideoTime,
  isValidTimecode: TimeValidator.isValidTimecode,
  isFirestoreTimestamp: TimeValidator.isFirestoreTimestamp,
  
  // 格式化函數
  formatDisplay: TimeFormatter.formatDisplayTime,
  formatRelative: TimeFormatter.formatRelativeTime,
  
  // 常數
  FRAME_RATE: TimeConverter['FRAME_RATE'],
  MAX_VIDEO_DURATION: TimeConverter['MAX_VIDEO_DURATION']
};

export default timeUtils;
